import { Navigate, Route, Routes } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";
import { App as AntdApp, ConfigProvider, Spin } from "antd";
import zhCN from "antd/locale/zh_CN";
import { AppLayout } from "./components/AppLayout";
import { Accounts } from "./pages/Accounts";
import { Dashboard } from "./pages/Dashboard";
import { Exceptions } from "./pages/Exceptions";
import { Locations } from "./pages/Locations";
import { Login } from "./pages/Login";
import { PackingStation } from "./pages/PackingStation";
import { Picking } from "./pages/Picking";
import { Forbidden, NotFound } from "./pages/NotFound";
import { Reconciliation } from "./pages/Reconciliation";
import { Skus } from "./pages/Skus";
import { ScanWorkbench } from "./pages/ScanWorkbench";
import { StockBySku } from "./pages/StockBySku";
import { Stocktake } from "./pages/Stocktake";
import { TagStation } from "./pages/TagStation";
import { TraceTimeline } from "./pages/TraceTimeline";
import { canManage, SessionProvider, useSession } from "./session";

function Shell(): JSX.Element {
  const { user, ready } = useSession();
  if (!ready) {
    return (
      <Spin spinning tip="正在读取这一页…">
        <div style={{ minHeight: 240 }} />
      </Spin>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout />;
}

function ManageOnly({ children }: { children: JSX.Element }): JSX.Element {
  const { user } = useSession();
  if (!canManage(user)) return <Forbidden />;
  return children;
}

function AdminOnly({ children }: { children: JSX.Element }): JSX.Element {
  const { user } = useSession();
  if (user?.role !== "ADMIN") return <Forbidden />;
  return children;
}

export function App(): JSX.Element {
  return (
    <ConfigProvider locale={zhCN} theme={{ token: { colorPrimary: "#1677ff", borderRadius: 6 } }}>
      <AntdApp>
        <BrowserRouter>
          <SessionProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<Shell />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/stock" element={<StockBySku />} />
                <Route path="/scan" element={<ScanWorkbench />} />
                <Route path="/skus" element={<Skus />} />
                <Route path="/tag-station" element={<TagStation />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/picking" element={<Picking />} />
                <Route path="/packing" element={<PackingStation />} />
                <Route path="/stocktake" element={<Stocktake />} />
                <Route path="/timeline" element={<TraceTimeline />} />
                <Route path="/exceptions" element={<Exceptions />} />
                <Route path="/reconciliation" element={<ManageOnly><Reconciliation /></ManageOnly>} />
                <Route path="/accounts" element={<AdminOnly><Accounts /></AdminOnly>} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </SessionProvider>
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
}
